import { useState, useEffect, useMemo, useRef, Fragment } from "react";
import { FixedSizeList } from "react-window";
import { getCurrentPracticeId } from "../lib/api";
import { db } from "../lib/db";
import { getInitials, formatDOB, calcAge } from "../utils/chartHelpers";
import { FacesheetCaptureModal, IDInsuranceCaptureModal, PendingIntakesPanel } from "../components/IntakeCapture";
import SurgicalBookingModal from "../components/SurgicalBookingModal";

// ─── HELPERS ─────────────────────────────────────────────────────────────────
const TODAY_D = new Date();
const TODAY   = TODAY_D.toISOString().split("T")[0];
const NINETY_DAYS_AGO = new Date(TODAY_D - 90 * 864e5).toISOString().split("T")[0];

function daysSince(dateStr) {
  if (!dateStr) return null;
  return Math.floor((TODAY_D - new Date(dateStr + "T12:00:00")) / 864e5);
}
function daysUntil(dateStr) {
  if (!dateStr) return null;
  return Math.floor((new Date(dateStr + "T12:00:00") - TODAY_D) / 864e5);
}
function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}
function buildPatientMap(patients) {
  return Object.fromEntries((patients || []).map(p => [p.id, p]));
}

// ─── DESIGN TOKENS ───────────────────────────────────────────────────────────
const C = {
  amber:  "#f0a500", amberLt: "rgba(240,165,0,0.15)",
  teal:   "#0ea5a0", tealLt:  "rgba(14,165,160,0.12)",
  red:    "#e05252", redLt:   "rgba(224,82,82,0.12)",
  green:  "#22c55e", greenLt: "rgba(34,197,94,0.12)",
};

const URGENCY_META = {
  "Elective":                { label: "Elective",    color: C.green,  bg: C.greenLt },
  "Semi-urgent (2–4 weeks)": { label: "Semi-Urgent", color: C.amber,  bg: C.amberLt },
  "Urgent (< 1 week)":       { label: "Urgent",      color: C.red,    bg: C.redLt   },
  "Emergent":                { label: "Emergent",    color: "#fff",   bg: C.red     },
};

function UrgencyBadge({ urgency }) {
  const m = URGENCY_META[urgency] || URGENCY_META["Elective"];
  return (
    <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 4, color: m.color, background: m.bg, whiteSpace: "nowrap" }}>
      {m.label}
    </span>
  );
}

const TH = { fontSize: 11, fontWeight: 600, color: "var(--text-3)", textTransform: "uppercase", letterSpacing: "0.06em", cursor: "pointer", userSelect: "none", display: "flex", alignItems: "center", gap: 3 };

function SortHeader({ label, col, sort, onSort }) {
  const active = sort.col === col;
  return (
    <span
      style={{ ...TH, color: active ? C.teal : "var(--text-3)" }}
      onClick={() => onSort(col)}
    >
      {label}{active && <span style={{ fontSize: 9 }}>{sort.dir === "asc" ? " ↑" : " ↓"}</span>}
    </span>
  );
}

// ─── SKELETON ROW ─────────────────────────────────────────────────────────────
function SkeletonRows({ cols, rows = 4 }) {
  return (
    <>
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} style={{ display: "grid", gridTemplateColumns: cols, padding: "13px 18px", gap: 12, alignItems: "center", borderBottom: "1px solid var(--border)" }}>
          {Array.from({ length: cols.split(" ").length }, (__, j) => (
            <div key={j} style={{ height: 10, borderRadius: 4, background: "var(--navy-3)", width: j === 0 ? "80%" : "60%" }} />
          ))}
        </div>
      ))}
    </>
  );
}

function EmptyState({ msg }) {
  return <div style={{ padding: "28px 18px", textAlign: "center", color: "var(--text-3)", fontSize: 13 }}>{msg}</div>;
}

function ProviderFilter({ value, options, onChange }) {
  return (
    <select
      value={value}
      onChange={e => onChange(e.target.value)}
      onClick={e => e.stopPropagation()}
      style={{ background: "var(--navy-3)", color: "var(--text-2)", border: "1px solid var(--border-2)", borderRadius: 6, padding: "4px 8px", fontSize: 12, outline: "none", cursor: "pointer" }}
    >
      <option value="">All Providers</option>
      {options.map(p => <option key={p} value={p}>{p}</option>)}
    </select>
  );
}

// ─── COLLAPSIBLE PIPELINE PANEL ───────────────────────────────────────────────
function PipelinePanel({ title, accentColor, count, loading, children }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ border: "1px solid var(--border)", borderRadius: 10, overflow: "hidden", background: "var(--navy-2)" }}>
      <div
        onClick={() => setOpen(o => !o)}
        style={{
          display: "flex", alignItems: "center", gap: 12, padding: "13px 18px",
          cursor: "pointer", borderLeft: `4px solid ${accentColor}`,
          background: open ? "rgba(255,255,255,0.025)" : "transparent",
          userSelect: "none",
        }}
      >
        <span style={{ fontSize: 13, color: accentColor, fontWeight: 700 }}>{open ? "▼" : "▶"}</span>
        <span style={{ fontWeight: 700, fontSize: 14, color: "var(--text-1)", flex: 1 }}>{title}</span>
        {loading ? (
          <span style={{ fontSize: 11, color: "var(--text-3)" }}>Loading…</span>
        ) : (
          <span style={{
            fontSize: 11, fontWeight: 700, padding: "2px 10px", borderRadius: 12,
            background: count > 0 ? `${accentColor}22` : "rgba(255,255,255,0.06)",
            color: count > 0 ? accentColor : "var(--text-3)",
          }}>
            {count} {count === 1 ? "case" : "cases"}
          </span>
        )}
      </div>
      {open && <div style={{ borderTop: "1px solid var(--border)" }}>{children}</div>}
    </div>
  );
}

// ─── OR ENTRY FORM (inline coordinator confirmation) ──────────────────────────
function OREntryForm({ order, patient, practiceId, onConfirm, onCancel }) {
  const [form, setForm] = useState({
    scheduled_date: order?.details?.target_date || "",
    scheduled_time: "",
    surgeon:        order?.ordered_by || "",
    room:           order?.details?.facility   || "",
    notes:          "",
  });
  const [saving, setSaving]  = useState(false);
  const [error,  setError]   = useState("");
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleConfirm = async () => {
    if (!form.scheduled_date) { setError("Surgery date is required."); return; }
    setError("");
    setSaving(true);
    try {
      // Insert surgical case
      const { error: insertErr } = await db.from("surgical_cases").insert({
        practice_id:    practiceId,
        patient_id:     order.patient_id,
        patient_first:  patient?.first_name || "",
        patient_last:   patient?.last_name  || "",
        patient_mrn:    patient?.mrn        || "",
        procedure_name: order?.details?.procedure || order?.description || "",
        scheduled_date: form.scheduled_date,
        scheduled_time: form.scheduled_time || null,
        surgeon:        form.surgeon || null,
        room:           form.room    || null,
        case_status:    "scheduled",
      });
      if (insertErr) throw insertErr;

      // Mark order as scheduled
      await db
        .from("patient_orders")
        .update({ status: "scheduled", notes: form.notes || order?.notes || "" })
        .eq("id", order.id);

      onConfirm(order.id);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const FIELD = { fontSize: 12, color: "var(--text-3)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 3, display: "block" };
  const IN = { padding: "6px 9px", borderRadius: 5, border: "1px solid var(--border-2)", background: "var(--navy-4)", color: "var(--text-1)", fontSize: 12, outline: "none", width: "100%" };

  return (
    <div style={{ gridColumn: "1 / -1", padding: "14px 18px 16px", background: "rgba(14,165,160,0.06)", borderBottom: "1px solid var(--border)", borderTop: "1px solid var(--border)" }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: "var(--teal)", marginBottom: 12, textTransform: "uppercase", letterSpacing: "0.05em" }}>
        Enter OR Details — {patient ? `${patient.first_name} ${patient.last_name}` : "Patient"}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10, marginBottom: 10 }}>
        <label>
          <span style={FIELD}>Surgery date *</span>
          <input type="date" value={form.scheduled_date} onChange={e => set("scheduled_date", e.target.value)} style={IN} />
        </label>
        <label>
          <span style={FIELD}>Time</span>
          <input type="time" value={form.scheduled_time} onChange={e => set("scheduled_time", e.target.value)} style={IN} />
        </label>
        <label>
          <span style={FIELD}>Surgeon</span>
          <input value={form.surgeon} onChange={e => set("surgeon", e.target.value)} style={IN} placeholder="Surgeon name" />
        </label>
        <label>
          <span style={FIELD}>Room / Facility</span>
          <input value={form.room} onChange={e => set("room", e.target.value)} style={IN} placeholder="e.g. OR 3 · Desert SC" />
        </label>
      </div>
      <div style={{ marginBottom: 10 }}>
        <label>
          <span style={FIELD}>Notes</span>
          <input value={form.notes} onChange={e => set("notes", e.target.value)} style={IN} placeholder="Any coordination notes…" />
        </label>
      </div>
      {error && <div style={{ marginBottom: 8, fontSize: 11, color: "#e05252" }}>{error}</div>}
      <div style={{ display: "flex", gap: 8 }}>
        <button
          onClick={handleConfirm}
          disabled={saving}
          style={{ padding: "5px 16px", borderRadius: 6, border: "none", background: "var(--teal)", color: "#fff", cursor: saving ? "default" : "pointer", fontSize: 12, fontWeight: 600 }}
        >
          {saving ? "Saving…" : "Confirm Booking"}
        </button>
        <button
          onClick={onCancel}
          style={{ padding: "5px 14px", borderRadius: 6, border: "1px solid var(--border-2)", background: "transparent", color: "var(--text-2)", cursor: "pointer", fontSize: 12 }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

// ─── PANEL 1 — SURGERY ORDERED ────────────────────────────────────────────────
function SurgeryOrderedPanel({ practiceId, onNavigate }) {
  const [orders,       setOrders]       = useState([]);
  const [patientMap,   setPatientMap]   = useState({});
  const [loading,      setLoading]      = useState(true);
  const [sort,         setSort]         = useState({ col: "days_waiting", dir: "desc" });
  const [provider,     setProvider]     = useState("");
  const [bookingModal, setBookingModal] = useState(null); // order object | null
  const [orEntryId,    setOrEntryId]    = useState(null); // order.id | null

  useEffect(() => {
    if (!practiceId) { setLoading(false); return; }
    let cancelled = false;
    (async () => {
      setLoading(true);
      const { data: rows, error } = await db
        .from("patient_orders")
        .select("id, patient_id, description, details, ordered_by, order_date, priority, notes")
        .eq("practice_id", practiceId)
        .eq("order_type", "surgery")
        .eq("status", "active");

      if (cancelled) return;
      if (error || !rows?.length) { setLoading(false); return; }
      setOrders(rows);

      const ids = [...new Set(rows.map(o => o.patient_id).filter(id => id && /^[0-9a-f-]{36}$/i.test(id)))];
      if (ids.length) {
        const { data: pts } = await db.from("patients").select("id, first_name, last_name, mrn").in("id", ids);
        if (!cancelled) setPatientMap(buildPatientMap(pts));
      }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [practiceId]);

  const providers = [...new Set(orders.map(o => o.ordered_by).filter(Boolean))].sort();
  const base      = provider ? orders.filter(o => o.ordered_by === provider) : orders;

  const sorted = [...base].sort((a, b) => {
    let av, bv;
    if (sort.col === "days_waiting") {
      av = daysSince(a.order_date) ?? 0; bv = daysSince(b.order_date) ?? 0;
    } else if (sort.col === "target_date") {
      av = a.details?.target_date || ""; bv = b.details?.target_date || "";
    } else if (sort.col === "patient_name") {
      const pa = patientMap[a.patient_id]; const pb = patientMap[b.patient_id];
      av = pa ? `${pa.last_name}${pa.first_name}` : ""; bv = pb ? `${pb.last_name}${pb.first_name}` : "";
    } else if (sort.col === "urgency") {
      const order = ["Emergent", "Urgent (< 1 week)", "Semi-urgent (2–4 weeks)", "Elective"];
      av = order.indexOf(a.details?.urgency || "Elective");
      bv = order.indexOf(b.details?.urgency || "Elective");
    } else { av = ""; bv = ""; }
    const cmp = av < bv ? -1 : av > bv ? 1 : 0;
    return sort.dir === "asc" ? cmp : -cmp;
  });

  const toggle = (col) => setSort(s => ({ col, dir: s.col === col && s.dir === "desc" ? "asc" : "desc" }));
  const COLS = "2fr 2fr 1.5fr 1.5fr 1fr 1.5fr 110px";

  return (
  <>
    <PipelinePanel title="Surgery Ordered — Awaiting Scheduling" accentColor={C.amber} count={orders.length} loading={loading}>
      <div style={{ padding: "10px 18px 8px", display: "flex", gap: 8 }}>
        <ProviderFilter value={provider} options={providers} onChange={setProvider} />
      </div>
      {loading ? <SkeletonRows cols={COLS} /> : sorted.length === 0 ? (
        <EmptyState msg="No active surgery orders." />
      ) : (
        <>
          <div style={{ display: "grid", gridTemplateColumns: COLS, padding: "6px 18px", gap: 12, borderBottom: "1px solid var(--border)" }}>
            <SortHeader label="Patient"     col="patient_name" sort={sort} onSort={toggle} />
            <SortHeader label="Procedure"   col="procedure"    sort={sort} onSort={toggle} />
            <SortHeader label="Target Date" col="target_date"  sort={sort} onSort={toggle} />
            <SortHeader label="Ordered By"  col="ordered_by"   sort={sort} onSort={toggle} />
            <SortHeader label="Waiting"     col="days_waiting" sort={sort} onSort={toggle} />
            <SortHeader label="Urgency"     col="urgency"      sort={sort} onSort={toggle} />
            <span />
          </div>
          {sorted.map(o => {
            const pt         = patientMap[o.patient_id];
            const proc       = o.details?.procedure || o.description || "—";
            const urgency    = o.details?.urgency || "Elective";
            const days       = daysSince(o.order_date);
            const dColor     = days == null ? "var(--text-3)" : days > 14 ? C.red : days > 7 ? C.amber : C.green;
            const requested  = !!o.details?.booking_requested_at;
            return (
              <Fragment key={o.id}>
                <div style={{ display: "grid", gridTemplateColumns: COLS, padding: "10px 18px", gap: 12, alignItems: "center", borderBottom: orEntryId === o.id ? "none" : "1px solid var(--border)", fontSize: 13 }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{pt ? `${pt.first_name} ${pt.last_name}` : "Unknown"}</div>
                    {pt?.mrn && <div style={{ fontSize: 11, color: "var(--text-3)", fontFamily: "monospace" }}>{pt.mrn}</div>}
                  </div>
                  <span style={{ color: "var(--text-1)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{proc}</span>
                  <span style={{ color: "var(--text-2)" }}>{o.details?.target_date || "—"}</span>
                  <span style={{ color: "var(--text-2)" }}>{o.ordered_by || "—"}</span>
                  <span style={{ color: dColor, fontWeight: 600 }}>{days != null ? `${days}d` : "—"}</span>
                  <UrgencyBadge urgency={urgency} />
                  <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-start" }}>
                    {requested ? (
                      <>
                        <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 4, color: C.amber, background: C.amberLt, whiteSpace: "nowrap" }}>
                          Awaiting Coordinator
                        </span>
                        <button
                          onClick={() => setOrEntryId(id => id === o.id ? null : o.id)}
                          style={{ padding: "3px 8px", borderRadius: 5, border: `1px solid ${C.teal}60`, background: "transparent", color: C.teal, cursor: "pointer", fontSize: 10, fontWeight: 600, whiteSpace: "nowrap" }}
                        >
                          {orEntryId === o.id ? "Cancel" : "Enter OR Details"}
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={() => setBookingModal(o)}
                        style={{ padding: "5px 10px", borderRadius: 6, border: `1px solid ${C.amber}60`, background: "transparent", color: C.amber, cursor: "pointer", fontSize: 11, fontWeight: 600, whiteSpace: "nowrap" }}
                      >
                        Book Surgery →
                      </button>
                    )}
                  </div>
                </div>
                {orEntryId === o.id && (
                  <OREntryForm
                    order={o}
                    patient={pt}
                    practiceId={practiceId}
                    onConfirm={(orderId) => {
                      setOrders(prev => prev.filter(x => x.id !== orderId));
                      setOrEntryId(null);
                    }}
                    onCancel={() => setOrEntryId(null)}
                  />
                )}
              </Fragment>
            );
          })}
        </>
      )}
    </PipelinePanel>
    {bookingModal && (
      <SurgicalBookingModal
        order={bookingModal}
        patient={patientMap[bookingModal.patient_id]}
        onClose={() => setBookingModal(null)}
        onSent={(updatedOrder) => {
          setOrders(prev => prev.map(o => o.id === updatedOrder.id ? updatedOrder : o));
          setBookingModal(null);
        }}
      />
    )}
  </>
  );
}

// ─── PANEL 2 — SCHEDULED FOR SURGERY ─────────────────────────────────────────
function ScheduledCasesPanel({ practiceId, onNavigate }) {
  const [cases,    setCases]    = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [sort,     setSort]     = useState({ col: "scheduled_date", dir: "asc" });
  const [provider, setProvider] = useState("");

  useEffect(() => {
    if (!practiceId) { setLoading(false); return; }
    db
      .from("surgical_cases")
      .select("id, patient_id, patient_first, patient_last, patient_mrn, procedure_name, scheduled_date, scheduled_time, surgeon, room, case_status")
      .eq("practice_id", practiceId)
      .eq("case_status", "scheduled")
      .order("scheduled_date", { ascending: true })
      .then(({ data, error }) => {
        if (!error && data) setCases(data);
        setLoading(false);
      });
  }, [practiceId]);

  const providers = [...new Set(cases.map(c => c.surgeon).filter(Boolean))].sort();
  const base      = provider ? cases.filter(c => c.surgeon === provider) : cases;

  const sorted = [...base].sort((a, b) => {
    let av, bv;
    if (sort.col === "scheduled_date")  { av = a.scheduled_date || ""; bv = b.scheduled_date || ""; }
    else if (sort.col === "surgeon")    { av = a.surgeon || ""; bv = b.surgeon || ""; }
    else if (sort.col === "patient_name") { av = `${a.patient_last}${a.patient_first}`; bv = `${b.patient_last}${b.patient_first}`; }
    else { av = ""; bv = ""; }
    const cmp = av < bv ? -1 : av > bv ? 1 : 0;
    return sort.dir === "asc" ? cmp : -cmp;
  });

  const toggle = (col) => setSort(s => ({ col, dir: s.col === col && s.dir === "asc" ? "desc" : "asc" }));
  const COLS = "2fr 2fr 1.5fr 1.5fr 1.5fr 1fr 110px";

  return (
    <PipelinePanel title="Scheduled for Surgery" accentColor={C.teal} count={cases.length} loading={loading}>
      <div style={{ padding: "10px 18px 8px", display: "flex", gap: 8 }}>
        <ProviderFilter value={provider} options={providers} onChange={setProvider} />
      </div>
      {loading ? <SkeletonRows cols={COLS} /> : sorted.length === 0 ? (
        <EmptyState msg="No cases scheduled." />
      ) : (
        <>
          <div style={{ display: "grid", gridTemplateColumns: COLS, padding: "6px 18px", gap: 12, borderBottom: "1px solid var(--border)" }}>
            <SortHeader label="Patient"      col="patient_name"   sort={sort} onSort={toggle} />
            <SortHeader label="Procedure"    col="procedure"      sort={sort} onSort={toggle} />
            <SortHeader label="Surgery Date" col="scheduled_date" sort={sort} onSort={toggle} />
            <SortHeader label="Surgeon"      col="surgeon"        sort={sort} onSort={toggle} />
            <span style={TH}>Room / Facility</span>
            <SortHeader label="Days Until"   col="scheduled_date" sort={sort} onSort={toggle} />
            <span />
          </div>
          {sorted.map(c => {
            const days = daysUntil(c.scheduled_date);
            const dColor = days == null ? "var(--text-3)" : days <= 0 ? C.red : days <= 3 ? C.amber : C.teal;
            const dLabel = days == null ? "—" : days === 0 ? "Today" : days < 0 ? `${Math.abs(days)}d ago` : `${days}d`;
            return (
              <div key={c.id} style={{ display: "grid", gridTemplateColumns: COLS, padding: "10px 18px", gap: 12, alignItems: "center", borderBottom: "1px solid var(--border)", fontSize: 13 }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {c.patient_first} {c.patient_last}
                  </div>
                  {c.patient_mrn && <div style={{ fontSize: 11, color: "var(--text-3)", fontFamily: "monospace" }}>{c.patient_mrn}</div>}
                </div>
                <span style={{ color: "var(--text-1)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.procedure_name || "—"}</span>
                <span style={{ color: "var(--text-2)" }}>{fmtDate(c.scheduled_date)}{c.scheduled_time ? ` · ${c.scheduled_time}` : ""}</span>
                <span style={{ color: "var(--text-2)" }}>{c.surgeon || "—"}</span>
                <span style={{ color: "var(--text-3)" }}>{c.room || "—"}</span>
                <span style={{ color: dColor, fontWeight: 600 }}>{dLabel}</span>
                <button
                  onClick={() => onNavigate?.("asc-board")}
                  style={{ padding: "5px 10px", borderRadius: 6, border: `1px solid ${C.teal}60`, background: "transparent", color: C.teal, cursor: "pointer", fontSize: 11, fontWeight: 600, whiteSpace: "nowrap" }}
                >
                  Open Case
                </button>
              </div>
            );
          })}
        </>
      )}
    </PipelinePanel>
  );
}

// ─── PANEL 3 — SURGERY COMPLETED ─────────────────────────────────────────────
function CompletedSurgeriesPanel({ practiceId, onPatientSelect }) {
  const [history,    setHistory]    = useState([]);
  const [patientMap, setPatientMap] = useState({});
  const [loading,    setLoading]    = useState(true);
  const [sort,       setSort]       = useState({ col: "surgery_date", dir: "desc" });
  const [provider,   setProvider]   = useState("");
  const [loadAll,    setLoadAll]    = useState(false);

  useEffect(() => {
    if (!practiceId) { setLoading(false); return; }
    let cancelled = false;
    (async () => {
      setLoading(true);
      let q = db
        .from("surgical_history")
        .select("id, procedure_name, surgery_date, surgeon, patient_id")
        .eq("practice_id", practiceId)
        .order("surgery_date", { ascending: false });
      if (!loadAll) q = q.gte("surgery_date", NINETY_DAYS_AGO);

      const { data: hist, error } = await q;
      if (cancelled) return;
      if (error || !hist?.length) { setLoading(false); return; }
      setHistory(hist);

      const ids = [...new Set(hist.map(h => h.patient_id).filter(id => id && /^[0-9a-f-]{36}$/i.test(id)))];
      if (ids.length) {
        const { data: pts } = await db.from("patients").select("id, first_name, last_name, mrn").in("id", ids);
        if (!cancelled) setPatientMap(buildPatientMap(pts));
      }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [practiceId, loadAll]);

  const providers = [...new Set(history.map(h => h.surgeon).filter(Boolean))].sort();
  const base      = provider ? history.filter(h => h.surgeon === provider) : history;

  const sorted = [...base].sort((a, b) => {
    let av, bv;
    if (sort.col === "surgery_date")   { av = a.surgery_date || ""; bv = b.surgery_date || ""; }
    else if (sort.col === "surgeon")   { av = a.surgeon || ""; bv = b.surgeon || ""; }
    else if (sort.col === "patient_name") {
      const pa = patientMap[a.patient_id]; const pb = patientMap[b.patient_id];
      av = pa ? `${pa.last_name}${pa.first_name}` : ""; bv = pb ? `${pb.last_name}${pb.first_name}` : "";
    }
    else { av = ""; bv = ""; }
    const cmp = av < bv ? -1 : av > bv ? 1 : 0;
    return sort.dir === "asc" ? cmp : -cmp;
  });

  const toggle = (col) => setSort(s => ({ col, dir: s.col === col && s.dir === "desc" ? "asc" : "desc" }));
  const COLS = "2fr 2fr 1.5fr 1.5fr 1fr 110px";

  return (
    <PipelinePanel title="Surgery Completed — Last 90 Days" accentColor="var(--text-3)" count={history.length} loading={loading}>
      <div style={{ padding: "10px 18px 8px", display: "flex", gap: 8 }}>
        <ProviderFilter value={provider} options={providers} onChange={setProvider} />
      </div>
      {loading ? <SkeletonRows cols={COLS} /> : sorted.length === 0 ? (
        <EmptyState msg={loadAll ? "No surgical history found." : "No surgeries in the last 90 days."} />
      ) : (
        <>
          <div style={{ display: "grid", gridTemplateColumns: COLS, padding: "6px 18px", gap: 12, borderBottom: "1px solid var(--border)" }}>
            <SortHeader label="Patient"      col="patient_name" sort={sort} onSort={toggle} />
            <SortHeader label="Procedure"    col="procedure"    sort={sort} onSort={toggle} />
            <SortHeader label="Surgery Date" col="surgery_date" sort={sort} onSort={toggle} />
            <SortHeader label="Surgeon"      col="surgeon"      sort={sort} onSort={toggle} />
            <SortHeader label="Days Since"   col="surgery_date" sort={sort} onSort={toggle} />
            <span />
          </div>
          {sorted.map(h => {
            const pt   = patientMap[h.patient_id];
            const days = daysSince(h.surgery_date);
            return (
              <div key={h.id} style={{ display: "grid", gridTemplateColumns: COLS, padding: "10px 18px", gap: 12, alignItems: "center", borderBottom: "1px solid var(--border)", fontSize: 13 }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{pt ? `${pt.first_name} ${pt.last_name}` : "—"}</div>
                  {pt?.mrn && <div style={{ fontSize: 11, color: "var(--text-3)", fontFamily: "monospace" }}>{pt.mrn}</div>}
                </div>
                <span style={{ color: "var(--text-1)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{h.procedure_name || "—"}</span>
                <span style={{ color: "var(--text-2)" }}>{fmtDate(h.surgery_date)}</span>
                <span style={{ color: "var(--text-2)" }}>{h.surgeon || "—"}</span>
                <span style={{ color: "var(--text-3)" }}>{days != null ? `${days}d` : "—"}</span>
                <button
                  onClick={() => pt && onPatientSelect({ ...pt, dob: pt.date_of_birth || "", sex: pt.sex || "", problems: [], allergies: [] })}
                  disabled={!pt}
                  style={{ padding: "5px 10px", borderRadius: 6, border: "1px solid var(--border-2)", background: "transparent", color: pt ? "var(--text-2)" : "var(--text-3)", cursor: pt ? "pointer" : "default", fontSize: 11, fontWeight: 600, whiteSpace: "nowrap" }}
                >
                  Open Chart
                </button>
              </div>
            );
          })}
        </>
      )}
      {!loadAll && (
        <div style={{ padding: "12px 18px", textAlign: "center" }}>
          <button
            onClick={() => setLoadAll(true)}
            style={{ padding: "6px 20px", borderRadius: 6, border: "1px solid var(--border-2)", background: "transparent", color: "var(--text-2)", cursor: "pointer", fontSize: 12 }}
          >
            Load all surgical history
          </button>
        </div>
      )}
    </PipelinePanel>
  );
}

// ─── SURGICAL PIPELINE ────────────────────────────────────────────────────────
function SurgicalPipeline({ practiceId, onPatientSelect, onNavigate }) {
  return (
    <div style={{ marginTop: 32 }}>
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 18, fontWeight: 600 }}>Surgical Pipeline</div>
        <div className="text-muted text-sm">Ordered, scheduled, and recently completed surgeries</div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <SurgeryOrderedPanel    practiceId={practiceId} onNavigate={onNavigate} />
        <ScheduledCasesPanel    practiceId={practiceId} onNavigate={onNavigate} />
        <CompletedSurgeriesPanel practiceId={practiceId} onPatientSelect={onPatientSelect} />
      </div>
    </div>
  );
}

// ─── SORTING ──────────────────────────────────────────────────────────────────
// Sorting is pure JS because the whole practice list is already in memory: one
// unpaginated `patients` query, virtualized for display by FixedSizeList. It also
// COULD NOT be a query change — db-allowlist.js gives `patients`
// orderable: ['last_name','created_at','id'], so DOB / MRN / insurance are not
// sortable server-side at all without an allowlist edit.

// numeric:true is what makes MRNs sort naturally — "OC-9" before "OC-10", where a
// plain string compare puts "OC-10" first. It also makes ISO dates sort
// chronologically whether or not the month/day are zero-padded (1980-1-2 vs
// 1980-12-2 compares 1 vs 12 as numbers), which is strictly safer here than a
// lexicographic compare. sensitivity:'base' keeps "van Dyke" and "Van Dyke"
// together instead of splitting the list by capitalization.
const collator = new Intl.Collator("en", { numeric: true, sensitivity: "base" });

// Returns the parts array, or null when every part is blank. null means EMPTY and
// is handled separately by the sorter — see sortPatients.
const parts = (arr) => (arr.some((v) => String(v ?? "").trim() !== "") ? arr.map((v) => String(v ?? "").trim()) : null);

// The chips this column renders are ICD-10 CODES, so the sort key is the code, not
// the diagnosis text — sorting by something the user cannot see reads as broken.
// The alphabetically-smallest code is used rather than problemMap's [0], because
// that array is in database order (the query has no .order()) and would make the
// sort non-deterministic across reloads.
function firstProblemCode(list) {
  const codes = (list || []).map((pr) => pr.icd10_code || pr.diagnosis || pr.name || "").filter(Boolean);
  if (!codes.length) return "";
  return codes.reduce((min, c) => (collator.compare(c, min) < 0 ? c : min), codes[0]);
}

// Each column yields a comparable parts array, or null for "nothing on file".
const SORT_COLUMNS = {
  patient:   { label: "patient name", value: (p) => parts([p.last_name, p.first_name]) },
  mrn:       { label: "MRN",          value: (p) => parts([p.mrn]) },
  // date_of_birth is a raw 'YYYY-MM-DD' string, not a Date: lib/db.js:12 registers
  // setTypeParser(1082) so DATE columns bypass node-pg's Date conversion. slice(0,10)
  // is belt-and-braces for the demo path, which reads the same column via Supabase.
  dob:       { label: "date of birth", value: (p) => parts([String(p.date_of_birth || "").slice(0, 10)]) },
  insurance: { label: "insurance",    value: (p) => parts([p.insurance_name]) },
  problems:  { label: "active problems", value: (p, ctx) => parts([firstProblemCode(ctx.problemMap[p.id])]) },
};

const compareParts = (a, b) => {
  for (let i = 0; i < Math.max(a.length, b.length); i++) {
    const c = collator.compare(a[i] || "", b[i] || "");
    if (c !== 0) return c;
  }
  return 0;
};

// EMPTY ALWAYS SORTS LAST, in both directions. A patient with no insurance on file
// is not "first alphabetically" — they are missing data, and flipping the arrow
// must never bury the rows that actually have something to read behind a wall of
// blanks. Direction applies only to the comparison between two present values.
//
// The original index is the final tiebreak, so equal rows keep the query's order
// (last_name asc) instead of shuffling between renders.
function sortPatients(rows, { key, dir }, ctx) {
  const col = SORT_COLUMNS[key];
  if (!col) return rows;
  const sign = dir === "desc" ? -1 : 1;
  return rows
    .map((p, i) => ({ p, i, v: col.value(p, ctx) }))
    .sort((a, b) => {
      if (a.v === null || b.v === null) {
        if (a.v === b.v) return a.i - b.i;
        return a.v === null ? 1 : -1;
      }
      const c = compareParts(a.v, b.v);
      return c !== 0 ? c * sign : a.i - b.i;
    })
    .map((x) => x.p);
}

// ─── PATIENTS PAGE ────────────────────────────────────────────────────────────
export default function PatientsPage({ onPatientSelect, onNavigate }) {
  const [patients,     setPatients]     = useState([]);
  const [problemMap,   setProblemMap]   = useState({});
  const [allergyMap,   setAllergyMap]   = useState({});
  const [loading,      setLoading]      = useState(true);
  const [query,        setQuery]        = useState("");
  const [showFacesheet,setShowFacesheet]= useState(false);
  const [practiceId,   setPracticeId]   = useState("");
  // Default matches the query's own order (last_name asc), so the first paint is
  // unchanged from before sorting existed.
  const [sort,         setSort]         = useState({ key: "patient", dir: "asc" });

  // Step 1 — get practiceId from session
  useEffect(() => {
    getCurrentPracticeId().then(pid => {
      if (pid) setPracticeId(pid);
      else setLoading(false);
    });
  }, []);

  // Step 2 — fetch patients + problems + allergies in parallel
  useEffect(() => {
    if (!practiceId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      const [ptsRes, probRes, algRes] = await Promise.all([
        db
          .from("patients")
          .select("id, mrn, first_name, last_name, date_of_birth, sex, insurance_name")
          .eq("practice_id", practiceId)
          .order("last_name", { ascending: true }),
        db
          .from("problem_list")
          .select("patient_id, diagnosis, icd10_code, status")
          .eq("practice_id", practiceId)
          .eq("status", "active")
          .is("deleted_at", null),
        db
          .from("allergies")
          .select("patient_id, allergen")
          .eq("practice_id", practiceId),
      ]);
      if (cancelled) return;

      if (!ptsRes.error && ptsRes.data)  setPatients(ptsRes.data);

      if (!probRes.error && probRes.data) {
        const map = {};
        probRes.data.forEach(p => { if (!map[p.patient_id]) map[p.patient_id] = []; map[p.patient_id].push(p); });
        setProblemMap(map);
      }
      if (!algRes.error && algRes.data) {
        const map = {};
        algRes.data.forEach(a => { if (!map[a.patient_id]) map[a.patient_id] = []; map[a.patient_id].push(a.allergen); });
        setAllergyMap(map);
      }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [practiceId]);

  const filtered = query.length >= 1
    ? patients.filter(p =>
        `${p.first_name} ${p.last_name}`.toLowerCase().includes(query.toLowerCase()) ||
        (p.mrn        || "").toLowerCase().includes(query.toLowerCase()) ||
        // Was `p.insurance`, which this row never has — the select asks for
        // insurance_name (:630), so the placeholder's "Search by ... insurance"
        // matched nothing for as long as it has been there.
        (p.insurance_name || "").toLowerCase().includes(query.toLowerCase())
      )
    : patients;

  const sorted = useMemo(
    () => sortPatients(filtered, sort, { problemMap }),
    [filtered, sort, problemMap],
  );

  const toggleSort = (key) =>
    setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));

  // Inherits the header row's type styling so a sortable header is visually
  // identical to a plain one apart from the arrow. The inactive arrow is drawn at
  // low opacity rather than hidden, so it is discoverable without a hover.
  const SortHeader = ({ id, children }) => {
    const active = sort.key === id;
    return (
      <button
        type="button"
        onClick={() => toggleSort(id)}
        title={`Sort by ${SORT_COLUMNS[id].label}`}
        style={{
          background: "none", border: 0, padding: 0, cursor: "pointer",
          font: "inherit", color: active ? "var(--text-1)" : "inherit",
          letterSpacing: "inherit", textTransform: "inherit",
          display: "inline-flex", alignItems: "center", gap: 4, minWidth: 0,
        }}
      >
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{children}</span>
        <span aria-hidden="true" style={{ fontSize: 8, opacity: active ? 1 : 0.3 }}>
          {active && sort.dir === "desc" ? "▼" : "▲"}
        </span>
      </button>
    );
  };

  const handleFacesheetParsed = (data) => {
    if (onNavigate) onNavigate("new-patient", { prefill: data, prefillSource: "facesheet" });
  };

  return (
    <div className="slide-in">
      <div className="row mb-24">
        <div>
          <div style={{ fontSize: 18, fontWeight: 600 }}>Patients</div>
          <div className="text-muted text-sm">
            {loading ? "Loading…" : `${patients.length} patient${patients.length !== 1 ? "s" : ""} on file`}
          </div>
        </div>
        <div className="ml-auto row gap-8">
          <div className="search-wrap" style={{ width: 280 }}>
            <span className="search-icon">⌕</span>
            <input
              className="search-input"
              placeholder="Search by name, MRN, insurance..."
              value={query}
              onChange={e => setQuery(e.target.value)}
            />
          </div>
          <button className="btn btn-secondary btn-sm" onClick={() => setShowFacesheet(true)}>📄 Scan Facesheet</button>
          <button className="btn btn-primary btn-sm" onClick={() => onNavigate && onNavigate("new-patient")}>+ New Patient</button>
        </div>
      </div>

      <div className="card" style={{ overflow: "hidden" }}>
        {/* Column headers */}
        <div style={{
          display: "grid",
          gridTemplateColumns: "2fr 1fr 1.5fr 1.5fr 1.5fr 1fr 1fr 90px",
          padding: "8px 16px",
          borderBottom: "1px solid var(--border)",
          fontSize: 11, fontWeight: 600, color: "var(--text-3)",
          textTransform: "uppercase", letterSpacing: "0.06em",
          background: "var(--navy-2)",
        }}>
          <SortHeader id="patient">Patient</SortHeader>
          <SortHeader id="mrn">MRN</SortHeader>
          <SortHeader id="dob">DOB / Age</SortHeader>
          <SortHeader id="insurance">Insurance</SortHeader>
          <SortHeader id="problems">Active Problems</SortHeader>
          {/* Last Visit / Next Appt are NOT sortable: their cells are hardcoded
              placeholders, not data (see the row render below). */}
          <span>Last Visit</span><span>Next Appt</span><span />
        </div>

        {loading ? (
          // Loading skeleton — same grid as patient rows
          <div>
            {[1, 2, 3, 4].map(i => (
              <div key={i} style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1.5fr 1.5fr 1.5fr 1fr 1fr 90px", padding: "14px 16px", gap: 12, borderBottom: "1px solid var(--border)", alignItems: "center" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div style={{ width: 30, height: 30, borderRadius: "50%", background: "var(--navy-3)", flexShrink: 0 }} />
                  <div style={{ height: 11, width: 110, borderRadius: 4, background: "var(--navy-3)" }} />
                </div>
                {[60, 90, 100, 80, 50, 60, 70].map((w, j) => (
                  <div key={j} style={{ height: 10, borderRadius: 4, background: "var(--navy-3)", width: `${w}%` }} />
                ))}
              </div>
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: "32px", textAlign: "center", color: "var(--text-3)", fontSize: 13 }}>
            {patients.length === 0
              ? "No patients found. Add your first patient to get started."
              : "No patients match your search."}
          </div>
        ) : (
          <FixedSizeList
            height={Math.min(sorted.length * 60, 540)}
            itemCount={sorted.length}
            itemSize={60}
            width="100%"
          >
            {({ index, style }) => {
              const p              = sorted[index];
              const activeProblems = problemMap[p.id] || [];
              const allergies      = allergyMap[p.id]  || [];
              return (
                <div
                  style={{
                    ...style,
                    display: "grid",
                    gridTemplateColumns: "2fr 1fr 1.5fr 1.5fr 1.5fr 1fr 1fr 90px",
                    alignItems: "center",
                    padding: "0 16px",
                    borderBottom: "1px solid var(--border)",
                    cursor: "pointer",
                    background: "var(--navy-2)",
                    transition: "background 120ms",
                  }}
                  onClick={() => onPatientSelect(p)}
                  onMouseEnter={e => e.currentTarget.style.background = "var(--navy-3)"}
                  onMouseLeave={e => e.currentTarget.style.background = "var(--navy-2)"}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                    <div className="avatar" style={{ width: 30, height: 30, fontSize: 11, flexShrink: 0 }}>
                      {getInitials(p.first_name, p.last_name)}
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {p.first_name} {p.last_name}
                      </div>
                      {allergies.length > 0 && (
                        <span className="text-xs text-red" style={{ whiteSpace: "nowrap" }}>
                          ⚠ {allergies.slice(0, 2).join(", ")}
                        </span>
                      )}
                    </div>
                  </div>
                  <span className="font-mono text-xs">{p.mrn || "—"}</span>
                  <span className="text-sm">{p.date_of_birth ? `${formatDOB(p.date_of_birth)} · ${calcAge(p.date_of_birth)}y` : "—"}</span>
                  {/* DEPRECATED COLUMN. patients.insurance_* is superseded by the
                      patient_insurances table (one row per policy, primary /
                      secondary / tertiary) — this cell moves there in Insurance
                      Sessions 2-3. Until then note the split: the New Patient form
                      writes insurance_primary (EMRShell.jsx:820) while only the two
                      importers write insurance_name, so hand-entered patients read
                      blank here even though the field name is now correct. */}
                  <span className="text-sm" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.insurance_name || "—"}</span>
                  <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                    {activeProblems.slice(0, 2).map((pr, i) => (
                      <span key={i} className="badge badge-red" style={{ fontSize: 10 }}>{pr.icd10_code}</span>
                    ))}
                    {activeProblems.length > 2 && (
                      <span className="badge badge-gray" style={{ fontSize: 10 }}>+{activeProblems.length - 2}</span>
                    )}
                  </div>
                  {/* UNWIRED, not empty data — these two have never been fetched.
                      Last Visit needs a clinical_notes read (max visit_date per
                      patient), Next Appt an appointments read (min future start).
                      Both are a follow-up, and both would hit the same 1000-row
                      db-query cap the problem_list query already does. Their
                      headers are deliberately not sortable. */}
                  <span className="text-sm text-muted" title="Not yet available">—</span>
                  <span className="text-sm text-muted" title="Not yet available">—</span>
                  <button
                    className="btn btn-secondary btn-sm"
                    style={{ whiteSpace: "nowrap" }}
                    onClick={e => { e.stopPropagation(); onPatientSelect({ ...p, dob: p.date_of_birth || "" }); }}
                  >
                    Open Chart
                  </button>
                </div>
              );
            }}
          </FixedSizeList>
        )}
      </div>

      <SurgicalPipeline
        practiceId={practiceId}
        onPatientSelect={onPatientSelect}
        onNavigate={onNavigate}
      />

      {showFacesheet && (
        <FacesheetCaptureModal
          onClose={() => setShowFacesheet(false)}
          onParsed={handleFacesheetParsed}
        />
      )}
    </div>
  );
}

// ─── DATE OF BIRTH ────────────────────────────────────────────────────────────
// The DOB input is <input type="date">, which accepts ONLY 'YYYY-MM-DD'. Handed
// anything else it renders BLANK while the raw value sits untouched in form state —
// the user would see an empty field, assume no DOB, hit Save, and ship the
// malformed string straight at a Postgres `date` column. So values arriving from
// OCR / portal intake are normalized on the way in, and anything unrecognized is
// left VERBATIM rather than blanked, so validateDob can reject it by name. Silently
// dropping a date someone scanned is worse than making them retype it.
//
// Deliberately mirrors _importDefs.js normalizeDate (minus Excel serials, which
// cannot reach a form field).
const pad2 = (v) => String(v).padStart(2, "0");

function toDateInput(raw) {
  const v = String(raw ?? "").trim();
  if (!v) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return v;
  const iso = v.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/); // 1980-1-2, or an ISO timestamp
  if (iso) return `${iso[1]}-${pad2(iso[2])}-${pad2(iso[3])}`;
  const us = v.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/); // 01/02/1980
  if (us) return `${us[3]}-${pad2(us[1])}-${pad2(us[2])}`;
  // Two-digit years are NOT guessed: "01/02/80" is 1980 or 2080 and inventing the
  // century on a date of birth is exactly the silent corruption this exists to stop.
  return v;
}

// Local calendar date — NOT toISOString(), which is UTC and returns tomorrow during
// an Arizona evening. Same reason chartHelpers.js todayISO() exists.
function todayLocalISO() {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

// "" means OK. EMPTY IS OK — date_of_birth is nullable and handleSavePatient already
// maps "" to null (EMRShell.jsx:810); a patient registered from a fax with no DOB
// legitimately has none. Malformed is never OK: it reaches a date column.
function validateDob(value) {
  const v = String(value || "").trim();
  if (!v) return "";
  if (!/^\d{4}-\d{2}-\d{2}$/.test(v)) {
    return `"${v}" is not a valid date — use the picker, or type YYYY-MM-DD.`;
  }
  const [y, m, d] = v.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  if (dt.getUTCFullYear() !== y || dt.getUTCMonth() !== m - 1 || dt.getUTCDate() !== d) {
    return `"${v}" is not a real calendar date.`; // e.g. 2026-02-31
  }
  if (v > todayLocalISO()) return "Date of birth cannot be in the future.";
  return "";
}

// ─── NEW PATIENT FORM ─────────────────────────────────────────────────────────
const EMPTY_FORM = {
  first: "", last: "", dob: "", sex: "M", phone: "", email: "",
  address: "", city: "", state: "", zip: "",
  insurance: "", memberId: "", insuranceGroup: "", copay: "",
  subscriberName: "", subscriberDob: "",
  emergencyContactName: "", emergencyContactPhone: "",
  referringProvider: "", primaryCareProvider: "",
  allergies: "", employer: "", attorney: "", notes: "",
};

// Maps OCR JSON keys → form keys for both facesheet and DL+insurance
function applyFacesheetData(parsed) {
  return {
    first:                parsed.first_name            || "",
    last:                 parsed.last_name             || "",
    dob:                  toDateInput(parsed.date_of_birth),
    sex:                  parsed.gender                || "M",
    phone:                parsed.phone                 || "",
    email:                parsed.email                 || "",
    address:              parsed.address               || "",
    city:                 parsed.city                  || "",
    state:                parsed.state                 || "",
    zip:                  parsed.zip                   || "",
    insurance:            parsed.insurance_name        || "",
    memberId:             parsed.insurance_member_id   || "",
    insuranceGroup:       parsed.insurance_group       || "",
    subscriberName:       parsed.subscriber_name       || "",
    subscriberDob:        toDateInput(parsed.subscriber_dob),
    emergencyContactName: parsed.emergency_contact_name  || "",
    emergencyContactPhone:parsed.emergency_contact_phone || "",
    referringProvider:    parsed.referring_provider    || "",
    primaryCareProvider:  parsed.primary_care_provider || "",
  };
}

function applyIDInsuranceData({ dl = {}, insurance = {} }) {
  return {
    first:          dl.first_name    || "",
    last:           dl.last_name     || "",
    dob:            toDateInput(dl.date_of_birth),
    address:        dl.address       || "",
    city:           dl.city          || "",
    state:          dl.state         || "",
    zip:            dl.zip           || "",
    insurance:      insurance.insurance_name  || "",
    memberId:       insurance.member_id       || "",
    insuranceGroup: insurance.group_number    || "",
    subscriberName: insurance.subscriber_name || "",
    copay:          insurance.copay           || "",
  };
}

function applyPortalIntake(sub) {
  const fd = sub.form_data || {};
  return {
    first:                fd.first_name            || "",
    last:                 fd.last_name             || "",
    dob:                  toDateInput(fd.date_of_birth),
    sex:                  fd.gender                || "M",
    phone:                fd.phone                 || "",
    email:                fd.email                 || "",
    address:              fd.address               || "",
    city:                 fd.city                  || "",
    state:                fd.state                 || "",
    zip:                  fd.zip                   || "",
    insurance:            fd.insurance_name        || "",
    memberId:             fd.insurance_member_id   || "",
    insuranceGroup:       fd.insurance_group       || "",
    subscriberName:       fd.subscriber_name       || "",
    copay:                fd.copay                 || "",
    allergies:            Array.isArray(fd.allergies) ? fd.allergies.join(", ") : (fd.allergies || ""),
    notes:                fd.chief_complaint       || "",
  };
}

export function NewPatientPage({ onSave, prefill, prefillSource, intakeMethod }) {
  const [form, setForm] = useState(() => prefill ? { ...EMPTY_FORM, ...applyFacesheetData(prefill) } : { ...EMPTY_FORM });
  // Track which fields were populated by OCR so we can highlight them
  const [ocrFields, setOcrFields] = useState(() => {
    if (!prefill) return new Set();
    const mapped = applyFacesheetData(prefill);
    return new Set(Object.keys(mapped).filter(k => mapped[k]));
  });
  const [sourceLabel, setSourceLabel] = useState(prefill ? (prefillSource === "facesheet" ? "📄 Facesheet OCR" : "🪪 ID + Insurance OCR") : null);
  const [showFacesheet, setShowFacesheet] = useState(false);
  const [dobError, setDobError] = useState("");
  const dobRef = useRef(null);
  const [showIDIns, setShowIDIns] = useState(false);
  const [showPortal, setShowPortal] = useState(false);
  // If intakeMethod prop is "portal", open portal panel on mount
  const portalOpenedRef = useRef(false);
  if (intakeMethod === "portal" && !portalOpenedRef.current) {
    portalOpenedRef.current = true;
    // use timeout so it opens after first render
    setTimeout(() => setShowPortal(true), 0);
  }

  const set = (k) => (e) => {
    setForm(f => ({ ...f, [k]: e.target.value }));
    if (k === "dob") setDobError("");
    // Clear OCR highlight when user edits the field manually
    setOcrFields(s => { const n = new Set(s); n.delete(k); return n; });
  };

  // Blocks ONLY on a malformed date. An empty DOB saves as null, which is legal.
  const handleSaveClick = () => {
    const err = validateDob(form.dob);
    setDobError(err);
    if (err) { dobRef.current?.focus(); return; } // the top Save button is above the field
    onSave(form);
  };

  const applyOCR = (data, source, mapFn) => {
    const mapped = mapFn(data);
    setForm(f => ({ ...f, ...mapped }));
    setOcrFields(new Set(Object.keys(mapped).filter(k => mapped[k])));
    setSourceLabel(source === "facesheet" ? "📄 Facesheet OCR" : source === "id-insurance" ? "🪪 ID + Insurance OCR" : "📋 Portal Intake");
  };

  const inputStyle = (key) => ({
    background: ocrFields.has(key) ? "rgba(251, 191, 36, 0.12)" : undefined,
    borderColor: ocrFields.has(key) ? "rgba(251, 191, 36, 0.5)" : undefined,
    transition: "background 300ms, border-color 300ms",
  });

  const field = (label, key, opts = {}) => (
    <div key={key} className="field">
      <label className="field-label">
        {label}{opts.required && <span style={{ color: "var(--red)", marginLeft: 2 }}>*</span>}
        {ocrFields.has(key) && <span style={{ fontSize: 9, color: "#f59e0b", marginLeft: 6, fontWeight: 700, letterSpacing: "0.04em" }}>OCR</span>}
      </label>
      {opts.type === "select"
        ? <select className="field-input field-select" style={inputStyle(key)} value={form[key]} onChange={set(key)}>{opts.options.map(o => <option key={o} value={o}>{o}</option>)}</select>
        : <input
            className="field-input"
            ref={opts.inputRef}
            type={opts.type || "text"}
            max={opts.max}
            style={{ ...inputStyle(key), ...(opts.error ? { borderColor: "var(--red)" } : null) }}
            placeholder={opts.placeholder || ""}
            value={form[key]}
            onChange={set(key)}
          />
      }
      {opts.error && (
        <div className="text-xs" style={{ color: "var(--red)", marginTop: 4 }}>{opts.error}</div>
      )}
    </div>
  );

  const intakeBtns = [
    { icon: "📄", label: "Scan Facesheet",     hint: "OCR a facesheet document",         onClick: () => setShowFacesheet(true) },
    { icon: "🪪", label: "Scan ID & Insurance", hint: "Driver's license + insurance card", onClick: () => setShowIDIns(true)    },
    { icon: "📋", label: "From Portal Intake",  hint: "Use a completed portal form",       onClick: () => setShowPortal(true)   },
    { icon: "✏️", label: "Enter Manually",      hint: "Fill in form fields below",         onClick: null                        },
  ];

  return (
    <div className="slide-in">
      <div className="row mb-24">
        <div>
          <div style={{ fontSize: 18, fontWeight: 600 }}>New Patient Registration</div>
          <div className="text-muted text-sm">
            {sourceLabel
              ? <span style={{ color: "#f59e0b" }}>{sourceLabel} — yellow fields were auto-populated. Review before saving.</span>
              : "Select an intake method or fill in manually"}
          </div>
        </div>
        <div className="ml-auto row gap-8">
          <button className="btn btn-secondary">Cancel</button>
          <button className="btn btn-primary" onClick={handleSaveClick}>Save Patient →</button>
        </div>
      </div>

      {/* Intake method selector */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header"><span className="card-title">How would you like to add this patient?</span></div>
        <div className="card-body" style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          {intakeBtns.map((b, i) => (
            <button
              key={i}
              className="btn btn-secondary"
              style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 16px" }}
              onClick={b.onClick || undefined}
              disabled={!b.onClick}
            >
              <span style={{ fontSize: 16 }}>{b.icon}</span>
              <div style={{ textAlign: "left" }}>
                <div style={{ fontWeight: 600, fontSize: 13 }}>{b.label}</div>
                <div style={{ fontSize: 11, color: "var(--text-3)", fontWeight: 400 }}>{b.hint}</div>
              </div>
            </button>
          ))}
        </div>
      </div>

      <div className="col gap-16">
        {/* Personal Information */}
        <div className="card">
          <div className="card-header"><span className="card-title">Personal Information</span></div>
          <div className="card-body">
            <div className="grid-3" style={{ gap: 16 }}>
              {field("First Name",    "first",  { required: true, placeholder: "First name" })}
              {field("Last Name",     "last",   { required: true, placeholder: "Last name" })}
              {/* type="date" gives a picker that can only emit YYYY-MM-DD; max stops
                  a future DOB at the widget. validateDob still runs on save — OCR
                  and portal intake write this field without touching the widget. */}
              {field("Date of Birth", "dob",    { required: true, type: "date", max: todayLocalISO(), error: dobError, inputRef: dobRef })}
              {field("Sex",           "sex",    { type: "select", options: ["M", "F", "Other"] })}
              {field("Phone",         "phone",  { placeholder: "480-555-0000" })}
              {field("Email",         "email",  { placeholder: "patient@email.com" })}
            </div>
          </div>
        </div>

        {/* Address */}
        <div className="card">
          <div className="card-header"><span className="card-title">Address</span></div>
          <div className="card-body">
            <div className="grid-3" style={{ gap: 16 }}>
              {field("Street Address", "address", { placeholder: "123 Main St" })}
              {field("City",           "city",    { placeholder: "Phoenix" })}
              {field("State",          "state",   { placeholder: "AZ" })}
              {field("ZIP Code",       "zip",     { placeholder: "85001" })}
            </div>
          </div>
        </div>

        {/* Insurance */}
        <div className="card">
          <div className="card-header"><span className="card-title">Insurance</span></div>
          <div className="card-body">
            <div className="grid-3" style={{ gap: 16 }}>
              {field("Insurance Carrier",  "insurance",      { placeholder: "e.g. Blue Cross Blue Shield" })}
              {field("Member ID",          "memberId",       { placeholder: "Member ID number" })}
              {field("Group Number",       "insuranceGroup", { placeholder: "Group number" })}
              {field("Copay ($)",          "copay",          { placeholder: "0" })}
              {field("Subscriber Name",    "subscriberName", { placeholder: "If different from patient" })}
              {field("Subscriber DOB",     "subscriberDob",  { placeholder: "YYYY-MM-DD" })}
            </div>
          </div>
        </div>

        {/* Providers & Emergency Contact */}
        <div className="card">
          <div className="card-header"><span className="card-title">Providers & Emergency Contact</span></div>
          <div className="card-body">
            <div className="grid-3" style={{ gap: 16 }}>
              {field("Referring Provider",     "referringProvider",    { placeholder: "Referring physician" })}
              {field("Primary Care Provider",  "primaryCareProvider",  { placeholder: "PCP name" })}
              {field("Emergency Contact",      "emergencyContactName", { placeholder: "Contact name" })}
              {field("Emergency Phone",        "emergencyContactPhone",{ placeholder: "10-digit phone" })}
            </div>
          </div>
        </div>

        {/* Additional */}
        <div className="card">
          <div className="card-header"><span className="card-title">Additional</span></div>
          <div className="card-body">
            <div className="grid-3" style={{ gap: 16 }}>
              {field("Allergies",           "allergies", { placeholder: "e.g. Penicillin, NSAIDs" })}
              {field("Employer (WC cases)", "employer",  { placeholder: "Employer name if applicable" })}
              {field("Attorney (PI cases)", "attorney",  { placeholder: "Attorney name if applicable" })}
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header"><span className="card-title">Clinical Notes</span></div>
          <div className="card-body">
            <textarea className="field-input w-full" rows={3} placeholder="Any relevant notes for this patient..." value={form.notes} onChange={set("notes")} style={{ resize: "vertical" }} />
          </div>
        </div>

        <div className="row" style={{ justifyContent: "flex-end", gap: 8 }}>
          <button className="btn btn-secondary">Cancel</button>
          <button className="btn btn-primary" onClick={handleSaveClick}>Save Patient →</button>
        </div>
      </div>

      {/* OCR modals */}
      {showFacesheet && (
        <FacesheetCaptureModal
          onClose={() => setShowFacesheet(false)}
          onParsed={(data, src) => applyOCR(data, src, applyFacesheetData)}
        />
      )}
      {showIDIns && (
        <IDInsuranceCaptureModal
          onClose={() => setShowIDIns(false)}
          onParsed={(data, src) => applyOCR(data, src, applyIDInsuranceData)}
        />
      )}
      {showPortal && (
        <PendingIntakesPanel
          onClose={() => setShowPortal(false)}
          onCreateChart={(sub) => applyOCR(sub, "portal", applyPortalIntake)}
        />
      )}
    </div>
  );
}
